import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CModal, CModalHeader, CModalBody, CProgress, CButton } from '@coreui/react';
import { IoCloudUploadOutline, IoRefresh } from 'react-icons/io5';
import { FaInfoCircle, FaArrowLeft } from "react-icons/fa";
import { IoMdClose, IoMdDownload } from 'react-icons/io';
import { EstimateUploadDocument, EstimateUploadedFilesList, RejectUploadEstimate, GetSampleEstimateTemplate } from 'src/request/Estimate';
import useLoading from 'src/utils/useLoading';
import { showError } from 'src/utils/swalError';
import { trackEvent, mixPanelEvents } from 'src/utils/mixpanel';
import { usePusherChannel } from 'src/utils/usePusherChannel';
import { usePusher } from 'src/utils/PusherProvider';
const EstimateUpload = ({ showEstimateUpload, onClose, project_id }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);
  const Swal = require('sweetalert2');
  const { InlineLoading, startLoading, stopLoading, isLoading } = useLoading();

  const userId = localStorage.getItem('user_id');
  const { pusher } = usePusher();

  useEffect(() => {
    if (showEstimateUpload) {
      fetchUploadedList();
    }
  }, [showEstimateUpload]);

  useEffect(() => {
    if (!showEstimateUpload) {
      setStep(1);
    }
  }, [showEstimateUpload]);

  const handleFileListChange = useCallback((data) => {
    if (!data || Number(data.project_id) !== Number(project_id)) return;

    setUploadedFiles((prev) => {
      if (['completed', 'cancelled upload'].includes(data.is_status)) {
        return prev.filter((file) => file.upload_id !== data.upload_id);
      }

      const statusMap = ['final preview', 'finish parsing', 'initial preview', 'done processing'].includes(data.is_status)
        ? 'success'
        : data.is_status;

      const exists = prev.some((file) => file.upload_id === data.upload_id);
      if (exists) {
        return prev.map((file) => {
          if (file.upload_id === data.upload_id) {
            return {
              ...file,
              status: statusMap,
            };
          }
          return file;
        });
      }

      const pendingIndex = prev.findIndex((file) => !file.upload_id && file.name === data.file_name);
      if (pendingIndex > -1) {
        return prev.map((file, idx) => {
          if (idx === pendingIndex) {
            return {
              ...file,
              upload_id: data.upload_id,
              date: data.date_created,
              status: statusMap,
            };
          }
          return file;
        });
      }

      return [
        {
          name: data.file_name,
          date: data.date_created,
          upload_id: data.upload_id,
          fileObj: null,
          status: statusMap,
        },
        ...prev,
      ];
    });
  }, [project_id]);

  usePusherChannel(`private-estimate-importer-${userId}`, 'new-estimate-importer-status', handleFileListChange);

  const fetchUploadedList = async () => {
    startLoading();
    try {
      const response = await EstimateUploadedFilesList(project_id);
      if (response?.data?.data) {
        const filtered = response.data.data
          .filter(file => !['completed', 'cancelled upload'].includes(file.is_status))
          .map(file => ({
            name: file.file_name,
            date: file.date_created,
            upload_id: file.upload_id,
            fileObj: null,
            status: ['final preview', 'finish parsing', 'initial preview', 'done processing'].includes(file.is_status)
              ? 'success'
              : file.is_status,
          }));
        setUploadedFiles(filtered);
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        showError(error?.response?.data?.response, "Permission Denied");
        return;
      }
      console.error('Error fetching uploaded files list:', error);
    } finally {
      setTimeout(() => {
        stopLoading();
      }, 500);
    }
  };

  const handleFileSelect = async (file) => {
    const fileName = file.name;
    const tempFile = {
      name: fileName,
      status: 'pending',
      date: new Date().toLocaleDateString(),
      fileObj: file,
    };
    setUploadedFiles(prev => [tempFile, ...prev]);
    try {
      await EstimateUploadDocument({ project_id, pdfFile: file });

      setUploadedFiles(prev => [tempFile, ...prev]);
      trackEvent(mixPanelEvents.UPLOAD_IMPORTER, {
        'Source': 'Estimate Importer Upload',
        'AIMHI Version': 1,
        'project_id': project_id,
      });

      if (!pusher) {
        fetchUploadedList();
      }
    } catch (error) {
      console.error('Upload failed', error);
      setUploadedFiles(prev =>
        prev.map(f => f.name === fileName ? { ...f, status: 'error' } : f)
      );
      if (error?.response?.status === 403) {
        showError(error?.response?.data?.response, "Permission Denied");
        return;
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const renderProgressOrMessage = (status, upload_id) => {
    switch (status) {
      case 'success':
        return (
          <div className="flex items-center justify-between p-3 mt-1 border border-green-200 shadow-sm rounded-xl bg-green-50">
            <div className="flex items-center space-x-3">
              <svg className="w-6 h-6 text-aimhi-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <div className="text-base text-slate-800">
                <span className="font-semibold text-aimhi-primary">Upload Completed</span>
              </div>
            </div>
            <a
              href={`/#/estimate-validate?project_id=${project_id}&upload_id=${upload_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-white transition rounded-lg bg-aimhi-info hover:bg-aimhi-info/90 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Preview Estimate
            </a>
          </div>
        );
      case 'pending':
        return (
          <div className="mt-1">
            <CProgress animated color="aimhi-info" value={100} className="h-4 mb-1 bg-slate-300" />
          </div>
        );
      case 'rejected':
        return (
          <>
            <div className="mt-1">
              <CProgress color="danger" value={100} animated className="h-4 rounded" />
            </div>
            <div className="mt-1 text-xs text-aimhi-danger">
              Something went wrong during the upload. Please try again.
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const EstimateUploadInstructions = () => {
    const instructionList = [
      {
        parts: [
          { strong: "Download the Excel sample template" },
          " as a reference for the most optimized format. You may also use your own template."
        ]
      },
      {
        parts: [
          { strong: "Copy and paste your data" },
          " into the sample template to improve layout consistency."
        ]
      },
      {
        parts: [
          { strong: "Avoid using overlapping columns, merged cells, or heavy formatting" },
          " that may affect structure detection."
        ]
      },
      {
        parts: [
          { strong: "Save or export the completed file as a PDF (.pdf)" },
          "."
        ]
      },
      {
        parts: [
          "The system only accepts ",
          { strong: "PDF files" },
          " for upload, so exporting from Excel or Google Sheets to PDF is required."
        ]
      },
      {
        parts: [
          "Ensure the exported PDF contains ",
          { strong: "selectable text" },
          " (not scanned images or screenshots)."
        ]
      },
      {
        parts: [
          "When you're ready, click ",
          { strong: "‘Proceed to Upload’" },
          " to continue."
        ]
      }
    ];

    const handleDownloadTemplate = async () => {
      try {
        const result = await GetSampleEstimateTemplate();
        const downloadUrl = result.data.url;

        const response = await fetch(downloadUrl);

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "AIMHI-estimate-template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
      catch (error) {
        if (error?.response?.status === 403) {
          showError(error?.response?.data?.response, "Permission Denied");
          return;
        }
        console.error('Something went wrong please try again', error);
      }
    }

    return (
      <>
        <div className="w-full">
          <div className="p-6 mb-6 rounded-lg border-aimhi-info border-1 text-aimhi-textlight-neutral bg-blue-50">
            <div className="flex items-start mb-4">
              <div className="flex items-center justify-center w-8 h-8 mr-3 bg-white rounded-full">
                <FaInfoCircle size={16} />
              </div>
              <div>
                <h5 className="mb-1 text-base font-semibold">Instructions</h5>
                <p className="text-sm text-aimhi-textlight-neutral">
                  These steps are here to help ensure your file uploads smoothly.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="flex-1">
                <div className="p-4 bg-white border border-blue-100 rounded-md">
                  <strong>📥 Before You Upload Your Estimate</strong>
                  <p className="mb-4 text-sm text-gray-700">
                    You may follow your own estimate format. However, for faster and more accurate importing,
                    we recommend following the steps below and aligning your Excel or Google Sheet with our sample template.
                  </p>

                  <p>
                    👉 <strong>For best results, upload 7 pages at a time.</strong>
                    If your estimate is longer than 7 pages, please split it into separate files.
                  </p>

                  <ol className="space-y-3 list-none">
                    {instructionList.map((instruction, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-gray-800">
                        <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-bold text-blue-700 bg-blue-100 rounded-full">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          {instruction.parts.map((part, i) =>
                            typeof part === "string" ? (
                              <span key={i}>{part}</span>
                            ) : (
                              <strong key={i}>{part.strong}</strong>
                            )
                          )}
                        </div>
                      </li>
                    ))}
                    <div className="flex justify-center">
                      <button
                        className="flex items-center px-4 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded-md hover:bg-blue-700"
                        onClick={handleDownloadTemplate}
                      >
                        <span>Download Template</span>
                        <IoMdDownload className="ml-2" size={18} />
                      </button>
                    </div>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  };

  const renderUploadArea = () => {
    const handleDragOver = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const handleDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const files = Array.from(event.dataTransfer.files);
      if (files.length === 0) return;
      const maxSizeInBytes = 10 * 1024 * 1024;

      files.forEach((file) => {
        if (file.type !== 'application/pdf') {
          Swal.fire({
            icon: 'error',
            title: 'Invalid File Type',
            text: 'Only PDF files are allowed. Please upload a valid .pdf file.',
            confirmButtonText: 'OK'
          });
          return;
        }

        if (file.size > maxSizeInBytes) {
          Swal.fire({
            icon: 'error',
            title: 'File Too Large',
            text: 'The file exceeds the 10MB size limit. Please upload a smaller PDF file.',
            confirmButtonText: 'OK'
          });
          return;
        }

        handleFileSelect(file);
      })
    };

    const triggerClick = () => {
      fileInputRef.current.click();
    };
    return (
      <div
        className="p-5 text-center card justify-content-center align-items-center"
        role="button"
        onClick={triggerClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          borderStyle: 'dashed',
          borderWidth: '2px',
          borderColor: 'rgba(128, 128, 128, 0.527)',
          borderRadius: '12px',
          background: '#f2f2f2',
        }}
      >
        <IoCloudUploadOutline size={100} />
        <span className="mt-4 d-flex flex-column">
          <h4 className="font-weight-bold">Drop Files Here</h4>
          <span>or</span>
          <span>Click here to select files</span>
          <div className="px-4 py-2 mt-4 text-sm bg-white border rounded-lg shadow-sm border-aimhi-primary text-aimhi-primary">
            Max size: <span className="font-semibold text-aimhi-textlight-neutral">10MB</span> · File Type: <span className="font-semibold text-aimhi-danger">.pdf</span>
          </div>
        </span>
      </div>
    );
  };

  const cancelUpload = async (upload_id) => {
    if (!upload_id) return;
    const result = await Swal.fire({
      title: 'Cancel Upload',
      text: 'Are you sure you want to cancel?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'No, keep it',
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      try {
        await RejectUploadEstimate(upload_id);
        await Swal.fire({
          title: 'Cancelled',
          text: 'Uploaded file has been cancelled successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        fetchUploadedList();
      } catch (error) {
        if (error?.response?.status === 403) {
          showError(error?.response?.data?.response, "Permission Denied");
          return;
        }
        console.error('Cancel upload failed:', error);
        await Swal.fire({
          title: 'Error',
          text: 'Failed to cancel the upload. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  return (
    <CModal
      show={showEstimateUpload}
      size="lg"
      onClose={onClose}
      keyboard={false}
      closeOnBackdrop={false}
    >
      <CModalHeader closeButton>
        <span className="text-xl font-bold text-slate-800">Import Estimate</span>
      </CModalHeader>
      <CModalBody>
        {step === 1 ? (
          <div className="mb-6">
            {EstimateUploadInstructions()}
            <div className="flex justify-end mt-6">
              <CButton color="aimhi-textlight-neutral" onClick={() => setStep(2)} className="mt-3">
                Proceed to Upload
              </CButton>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={() => setStep(1)}
              className="inline-flex items-center gap-2 mb-2 text-sm font-medium text-blue-600 hover:text-blue-800 focus:outline-none"
            >
              <FaArrowLeft size={16} />
              <span className='underline'>Go Back </span>
            </button>

            {renderUploadArea()}
          </>
        )}

        {step === 2 && uploadedFiles.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-base font-semibold text-slate-800">Upload Status</h4>
              <div className="p-1 border rounded-full bg-gray-50 hover:bg-gray-100">
                <IoRefresh
                  size={24}
                  role="button"
                  className="text-aimhi-info"
                  onClick={fetchUploadedList}
                />
              </div>
            </div>

            <div className="divide-y divide-gray-300 max-h-[400px] overflow-auto">
              {isLoading ? (
                <div className="flex justify-center py-2">
                  <InlineLoading />
                </div>
              ) : (
                uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-start gap-4 py-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-sm text-gray-800">
                        <div>
                          <p className="m-0 font-medium">{file.name}</p>
                        </div>
                        <span className="text-xs text-gray-400">{file.date}</span>
                      </div>
                      {renderProgressOrMessage(file.status, file.upload_id)}
                    </div>

                    {file.status === 'pending' && (
                      <CButton className="mt-1 focus:outline-none">
                        <IoMdClose className="text-aimhi-textlight hover:text-gray-600" size={14} onClick={() => cancelUpload(file.upload_id)} />
                      </CButton>
                    )}

                    {file.status === 'error' && file.fileObj && (
                      <CButton
                        className="mt-1 focus:outline-none"
                        onClick={() => handleFileSelect(file.fileObj)}
                      >
                        <IoCloudUploadOutline className="text-aimhi-textlight hover:text-gray-600" size={14} />
                      </CButton>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <input
          type="file"
          accept=".pdf"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </CModalBody>
    </CModal>
  );
};

export default EstimateUpload;
